import React, { useEffect, useState } from 'react'
import { collection, query, where, onSnapshot, addDoc, doc, updateDoc, orderBy, getDocs } from 'firebase/firestore'
import { db } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'
import AppLayout from '../../components/layout/AppLayout'

type AttendanceType = 'late' | 'absent'
type Status = 'pending' | 'acknowledged'

interface AttendanceRequest {
  id: string
  userId: string
  userName: string
  userEmail: string
  crewId?: string
  supervisorId: string
  supervisorName: string
  supervisorEmail: string
  type: AttendanceType
  comment: string
  status: Status
  createdAt: string
  acknowledgedAt?: string
  acknowledgedBy?: string
  orgId: string
}

type ReportFilter = 'all' | 'late' | 'absent' | 'pending' | 'acknowledged'

export default function AttendancePage() {
  const { orgId, userData } = useAuth()
  const [showForm, setShowForm] = useState(false)
  const [type, setType] = useState<AttendanceType>('late')
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [supervisor, setSupervisor] = useState<any>(null)
  const [myRequests, setMyRequests] = useState<AttendanceRequest[]>([])
  const [supervisorInbox, setSupervisorInbox] = useState<AttendanceRequest[]>([])
  const [reportRequests, setReportRequests] = useState<AttendanceRequest[]>([])
  const [reportFilter, setReportFilter] = useState<ReportFilter>('all')
  const [view, setView] = useState<'mine' | 'supervisor' | 'report'>('mine')

  const isAdmin = userData?.role === 'admin'
  const isForemanOrAbove = ['admin', 'supervisor', 'superintendant', 'foreman'].includes(userData?.role || '')
  const isCrewLead = ['admin', 'foreman', 'supervisor', 'superintendant'].includes(userData?.role || '')

  useEffect(() => {
    if (!orgId || !userData) return

    // Load my recent requests
    const unsubMine = onSnapshot(
      query(
        collection(db, 'organizations', orgId, 'attendanceRequests'),
        where('userId', '==', userData.uid),
        orderBy('createdAt', 'desc')
      ),
      snap => setMyRequests(snap.docs.map(d => ({ id: d.id, ...d.data() } as AttendanceRequest))),
      (err) => console.error('Attendance my requests error:', err)
    )

    // Load supervisor inbox (pending requests from their crew)
    if (isForemanOrAbove) {
      const unsubSup = onSnapshot(
        query(
          collection(db, 'organizations', orgId, 'attendanceRequests'),
          where('supervisorId', '==', userData.uid),
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc')
        ),
        snap => setSupervisorInbox(snap.docs.map(d => ({ id: d.id, ...d.data() } as AttendanceRequest))),
        (err) => console.error('Attendance inbox error:', err)
      )
      return () => { unsubMine(); unsubSup() }
    }

    return () => unsubMine()
  }, [orgId, userData, isForemanOrAbove])

  // Load attendance report data for crew leads / admins
  useEffect(() => {
    if (!orgId || !userData || !isCrewLead) return

    const unsub = onSnapshot(
      query(
        collection(db, 'organizations', orgId, 'attendanceRequests'),
        orderBy('createdAt', 'desc')
      ),
      snap => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() } as AttendanceRequest))
        if (isAdmin) {
          setReportRequests(all)
        } else {
          // Filter to requests from users in the same crew as the supervisor
          const crewMembers = all.filter(r => r.crewId === userData.crewId)
          setReportRequests(crewMembers)
        }
      },
      (err) => console.error('Attendance report error:', err)
    )

    return () => unsub()
  }, [orgId, userData, isCrewLead, isAdmin])

  const loadSupervisor = async () => {
    if (!orgId || !userData) return
    if (userData.reportsTo) {
      const { doc: docFn, getDoc } = await import('firebase/firestore')
      const supDoc = docFn(db, 'organizations', orgId, 'users', userData.reportsTo)
      const snap = await getDoc(supDoc)
      if (snap.exists()) {
        setSupervisor({ id: snap.id, ...snap.data() })
        return
      }
    }
    // Fallback: find any supervisor/foreman above this user, excluding themselves
    const { getDocs: gd } = await import('firebase/firestore')
    const q = query(collection(db, 'organizations', orgId, 'users'), where('orgId', '==', orgId))
    const snap = await gd(q)
    const sup = snap.docs.find(d =>
      d.id !== userData.uid &&
      ['supervisor', 'superintendant'].includes(d.data().role || '')
    )
    if (sup) {
      setSupervisor({ id: sup.id, ...sup.data() })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgId || !userData || !supervisor) return
    // Capture values in case auth state changes mid-function
    const currentOrgId = orgId
    const currentUserData = userData
    const currentSupervisor = supervisor

    setSubmitting(true)
    try {
      await addDoc(collection(db, 'organizations', currentOrgId, 'attendanceRequests'), {
        userId: currentUserData.uid,
        userName: currentUserData.name,
        userEmail: currentUserData.email,
        crewId: currentUserData.crewId || '',
        supervisorId: currentSupervisor.id,
        supervisorName: currentSupervisor.name,
        supervisorEmail: currentSupervisor.email,
        type,
        comment,
        status: 'pending',
        createdAt: new Date().toISOString(),
        orgId: currentOrgId,
      })
      setSubmitted(true)
      setShowForm(false)
      setComment('')
      setType('late')
    } finally {
      setSubmitting(false)
    }
  }

  const handleAcknowledge = async (request: AttendanceRequest) => {
    if (!orgId || !userData) return
    await updateDoc(doc(db, 'organizations', orgId, 'attendanceRequests', request.id), {
      status: 'acknowledged',
      acknowledgedAt: new Date().toISOString(),
      acknowledgedBy: userData.uid,
    })
  }

  const formatTime = (iso: string) => {
    const d = new Date(iso)
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' ' + d.toLocaleDateString()
  }

  const filteredReport = reportRequests.filter(r => {
    if (reportFilter === 'all') return true
    if (reportFilter === 'late') return r.type === 'late'
    if (reportFilter === 'absent') return r.type === 'absent'
    if (reportFilter === 'pending') return r.status === 'pending'
    if (reportFilter === 'acknowledged') return r.status === 'acknowledged'
    return true
  })

  const renderView = () => {
    if (view === 'supervisor' && isForemanOrAbove) return renderSupervisorView()
    if (view === 'report' && isCrewLead) return renderReportView()
    return renderMineView()
  }

  const renderMineView = () => (
    <>
      {/* My Requests */}
      <div className="mb-8">
        <h3 className="text-sm font-semibold text-gray-600 mb-3">My Recent Requests</h3>
        {myRequests.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No attendance requests yet.</p>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {myRequests.map(r => (
              <div key={r.id} className="flex items-center justify-between px-5 py-4 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm ${r.type === 'late' ? 'bg-amber-500' : 'bg-red-500'}`}>
                    {r.userName?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{r.type === 'late' ? 'Running Late' : 'Will Not Be In Today'}</p>
                    <p className="text-sm text-gray-500">{formatTime(r.createdAt)}</p>
                    {r.comment && <p className="text-sm text-gray-600 mt-0.5 italic">"{r.comment}"</p>}
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${r.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                  {r.status === 'pending' ? 'Pending' : 'Acknowledged'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Report Attendance */}
      {!showForm ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-600 mb-4">Report Attendance</h3>
          <p className="text-sm text-gray-500 mb-4">Notify your supervisor if you will be late or absent today.</p>
          <div className="flex gap-4">
            <button onClick={() => { loadSupervisor(); setType('late'); setShowForm(true) }}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white py-6 rounded-xl font-semibold text-lg transition-colors flex flex-col items-center gap-2">
              <span className="text-2xl">⏰</span>
              Running Late
            </button>
            <button onClick={() => { loadSupervisor(); setType('absent'); setShowForm(true) }}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white py-6 rounded-xl font-semibold text-lg transition-colors flex flex-col items-center gap-2">
              <span className="text-2xl">❌</span>
              Not Coming In
            </button>
          </div>
          {!supervisor && myRequests.length === 0 && (
            <p className="text-xs text-gray-400 mt-3 text-center">Your supervisor must be assigned. Ask your admin to set your Reports To in the Users page.</p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600">
              {type === 'late' ? 'Report Running Late' : 'Report Absence'}
            </h3>
            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
          </div>
          {supervisor && (
            <p className="text-sm text-gray-500 mb-4">
              Notifying: <span className="font-medium text-gray-700">{supervisor.name}</span> ({supervisor.role})
            </p>
          )}
          {!supervisor && (
            <p className="text-sm text-red-500 mb-4">No supervisor found. Please contact your admin to assign a supervisor.</p>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Comments (optional)</label>
              <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3}
                placeholder={type === 'late' ? 'e.g., Running 30 minutes behind...' : 'e.g., Not feeling well, will be out all day...'}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" />
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowForm(false)}
                className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={submitting || !supervisor}
                className="flex-1 bg-accent text-white py-2.5 rounded-lg font-medium hover:bg-amber-700 disabled:bg-amber-300">
                {submitting ? 'Sending...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  )

  const renderSupervisorView = () => (
    <>
      {supervisorInbox.length > 0 && (
        <div className="mb-8">
          <h3 className="text-sm font-semibold text-red-600 mb-3">Pending Acknowledgments ({supervisorInbox.length})</h3>
          <div className="bg-red-50 rounded-xl border border-red-200 overflow-hidden">
            {supervisorInbox.map(r => (
              <div key={r.id} className="flex items-center justify-between px-5 py-4 border-b border-red-100 last:border-0">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm ${r.type === 'late' ? 'bg-amber-500' : 'bg-red-500'}`}>
                    {r.userName?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{r.userName}</p>
                    <p className="text-sm text-gray-500">
                      {r.type === 'late' ? 'Running Late' : 'Will Not Be In'} • {formatTime(r.createdAt)}
                    </p>
                    {r.comment && <p className="text-sm text-gray-600 mt-0.5 italic">"{r.comment}"</p>}
                  </div>
                </div>
                <button onClick={() => handleAcknowledge(r)}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700">
                  Acknowledge
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {supervisorInbox.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
          <p className="text-gray-400 text-sm">No pending acknowledgments.</p>
        </div>
      )}
    </>
  )

  const renderReportView = () => (
    <>
      {/* Filter bar */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {(['all', 'late', 'absent', 'pending', 'acknowledged'] as ReportFilter[]).map(f => (
          <button key={f} onClick={() => setReportFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${reportFilter === f ? 'bg-accent text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {filteredReport.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No attendance records found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-medium text-gray-600">Employee</th>
                <th className="text-left px-5 py-3 font-medium text-gray-600">Crew</th>
                <th className="text-left px-5 py-3 font-medium text-gray-600">Type</th>
                <th className="text-left px-5 py-3 font-medium text-gray-600">Comment</th>
                <th className="text-left px-5 py-3 font-medium text-gray-600">Submitted</th>
                <th className="text-left px-5 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-5 py-3 font-medium text-gray-600">Acknowledged</th>
              </tr>
            </thead>
            <tbody>
              {filteredReport.map(r => (
                <tr key={r.id} className="border-b border-gray-100">
                  <td className="px-5 py-3 font-medium text-gray-900">{r.userName}</td>
                  <td className="px-5 py-3 text-gray-600 text-xs">{r.crewId || '—'}</td>
                  <td className="px-5 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.type === 'late' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                      {r.type === 'late' ? 'Late' : 'Absent'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-500 text-xs italic max-w-xs truncate">{r.comment || '—'}</td>
                  <td className="px-5 py-3 text-gray-500">{formatTime(r.createdAt)}</td>
                  <td className="px-5 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-500 text-xs">
                    {r.acknowledgedAt ? formatTime(r.acknowledgedAt) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )

  return (
    <AppLayout title="Attendance">
      {/* View tabs (for crew leads and above) */}
      {isCrewLead && (
        <div className="mb-6 flex gap-2">
          <button onClick={() => setView('mine')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${view === 'mine' ? 'bg-accent text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            My Attendance
          </button>
          <button onClick={() => setView('supervisor')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${view === 'supervisor' ? 'bg-accent text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            My Inbox {supervisorInbox.length > 0 && <span className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{supervisorInbox.length}</span>}
          </button>
          <button onClick={() => setView('report')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${view === 'report' ? 'bg-accent text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            Attendance Report
          </button>
        </div>
      )}

      {/* Regular members just see the form */}
      {!isCrewLead && (
        <div className="mb-4 flex gap-2">
          <span className="px-4 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 text-gray-600">My Attendance</span>
        </div>
      )}

      {renderView()}
    </AppLayout>
  )
}
